import React, { useEffect, useState } from 'react';
import { PhoneIcon, MailIcon, MessageSquareIcon, CalendarIcon, StickyNoteIcon, ChevronDownIcon, ChevronUpIcon, CheckCircle2Icon, ClockIcon, ExternalLinkIcon, PlayCircleIcon, ArrowDownLeftIcon, ArrowUpRightIcon, StarIcon } from 'lucide-react';
import { Activity, CLIENT_NAME } from '../data/mockData';
interface TimelineItemProps {
  activity: Activity;
  isLast?: boolean;
  /** Increments each time the parent triggers Expand/Collapse All */
  expandAllToken?: number;
  /** Desired open state applied when expandAllToken changes */
  expandAllValue?: boolean;
}
export function TimelineItem({
  activity,
  isLast = false,
  expandAllToken = 0,
  expandAllValue = false
}: TimelineItemProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const isSms = activity.type === 'sms';
  // Clean, standardized subject line. For SMS the raw message body is noisy as a
  // title, so show a clear directional subject (who contacted who) instead.
  const displayTitle = isSms ? activity.smsDirection === 'inbound' ? `SMS from ${CLIENT_NAME}` : `SMS to ${CLIENT_NAME}` : activity.summary;
  // Format timestamp
  const dateObj = new Date(activity.timestamp);
  const dateStr = dateObj.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
  const timeStr = dateObj.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit'
  });
  // Format duration
  const formatDuration = (seconds?: number) => {
    if (!seconds) return null;
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}m ${s}s`;
  };
  // Determine styling based on activity type and status
  const getTypeConfig = () => {
    switch (activity.type) {
      case 'call':
        const isSuccess = activity.disposition === 'Connected';
        const isWarning = activity.disposition === 'Voicemail' || activity.disposition === 'Left Message';
        return {
          icon: <PhoneIcon className="w-5 h-5" data-id="element-179" />,
          iconBg: isSuccess ? 'bg-emerald-100 text-emerald-600 ring-emerald-50' : isWarning ? 'bg-amber-100 text-amber-600 ring-amber-50' : 'bg-red-100 text-red-600 ring-red-50',
          cardBorder: isSuccess ? 'border-l-4 border-l-emerald-500' : isWarning ? 'border-l-4 border-l-amber-500' : 'border-l-4 border-l-red-500',
          highlight: activity.isMeaningful
        };
      case 'email':
        const isOpened = activity.emailStatus === 'opened';
        return {
          icon: <MailIcon className="w-5 h-5" data-id="element-180" />,
          iconBg: 'bg-blue-100 text-blue-600 ring-blue-50',
          cardBorder: isOpened ? 'border-l-4 border-l-blue-400' : 'border-l-4 border-l-gray-300',
          highlight: false
        };
      case 'sms':
        return {
          icon: <MessageSquareIcon className="w-5 h-5" data-id="element-181" />,
          iconBg: 'bg-teal-100 text-teal-600 ring-teal-50',
          cardBorder: 'border-l-4 border-l-teal-400',
          highlight: false
        };
      case 'event':
        return {
          icon: <CalendarIcon className="w-5 h-5" data-id="element-182" />,
          iconBg: 'bg-indigo-100 text-indigo-600 ring-indigo-50',
          cardBorder: 'border-l-4 border-l-indigo-400',
          highlight: false
        };
      case 'note':
        return {
          icon: <StickyNoteIcon className="w-5 h-5" data-id="element-183" />,
          iconBg: 'bg-gray-100 text-gray-600 ring-gray-50',
          cardBorder: 'border-l-4 border-l-gray-300',
          highlight: false
        };
      case 'milestone':
        return {
          icon: <StarIcon className="w-5 h-5 fill-current" data-id="element-184" />,
          iconBg: 'bg-red-100 text-red-500 ring-red-50',
          cardBorder: 'border-l-4 border-l-red-500',
          highlight: true
        };
    }
  };
  const config = getTypeConfig();
  // Milestones are significant file markers, not expandable interactions
  const isMilestone = activity.type === 'milestone';
  const isExpandable = !isMilestone;
  // Apply Expand All / Collapse All from the parent without affecting milestones.
  useEffect(() => {
    if (expandAllToken > 0 && isExpandable) {
      setIsExpanded(expandAllValue);
    }
  }, [expandAllToken, expandAllValue, isExpandable]);
  return <div className="relative flex gap-6 group" data-id="element-185">
      {/* Timeline Line */}
      {!isLast && <div className="absolute left-6 top-12 bottom-0 w-px bg-gray-200 -ml-px group-hover:bg-gray-300 transition-colors" data-id="element-186" />}

      {/* Timeline Icon */}
      <div className="relative z-10 flex-shrink-0" data-id="element-187">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center ring-4 ${config.iconBg}`} data-id="element-188">
          {config.icon}
        </div>
      </div>

      {/* Content Card */}
      <div className="flex-1 pb-8" data-id="element-189">
        <div className={`bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden ${config.cardBorder} ${isExpandable ? 'cursor-pointer' : ''}`} onClick={isExpandable ? () => setIsExpanded(!isExpanded) : undefined} role={isExpandable ? 'button' : undefined} tabIndex={isExpandable ? 0 : undefined} aria-expanded={isExpandable ? isExpanded : undefined} onKeyDown={isExpandable ? e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setIsExpanded(!isExpanded);
        }
      } : undefined} data-id="element-190">
          {/* Collapsed Header */}
          <div className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-191">
            <div className="flex-1" data-id="element-192">
              <div className="flex items-center gap-2 mb-1" data-id="element-193">
                <span className="text-sm font-medium text-gray-500" data-id="element-194">
                  {dateStr} at {timeStr}
                </span>
                {activity.type === 'note' && activity.author && <span className="text-sm text-gray-400" data-id="element-195">
                    • by {activity.author}
                  </span>}
              </div>
              <h3 className={`text-base font-semibold ${config.highlight ? 'text-gray-900' : 'text-gray-800'}`} data-id="element-196">
                {displayTitle}
              </h3>

              {/* Type-specific quick metadata */}
              {activity.type === 'note' && !isExpanded && activity.details && <p className="text-sm text-gray-500 mt-1 line-clamp-1 italic" data-id="element-197">
                  {activity.details}
                </p>}

              {/* SMS: show the message body as a preview beneath the clean subject */}
              {isSms && !isExpanded && <p className="text-sm text-gray-500 mt-1 line-clamp-1" data-id="element-198">
                  {activity.summary}
                </p>}

              {/* Milestones aren't expandable, so surface details inline */}
              {isMilestone && activity.details && <p className="text-sm text-gray-500 mt-1" data-id="element-199">{activity.details}</p>}
            </div>

            {/* Right side metadata & badges */}
            <div className="flex items-center gap-3 sm:justify-end" data-id="element-200">
              {/* Call specific badges */}
              {activity.type === 'call' && <>
                  {activity.duration && <div className="flex items-center text-sm font-medium text-gray-700 bg-gray-100 px-2.5 py-1 rounded-md" data-id="element-201">
                      <ClockIcon className="w-3.5 h-3.5 mr-1.5 text-gray-500" data-id="element-202" />
                      {formatDuration(activity.duration)}
                    </div>}
                  {activity.disposition && <span className={`text-xs font-bold px-2.5 py-1 rounded-full border
                      ${activity.disposition === 'Connected' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : activity.disposition === 'Voicemail' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-red-50 text-red-700 border-red-200'}`} data-id="element-203">
                      {activity.disposition}
                    </span>}
                  {activity.isMeaningful && <div className="flex items-center text-emerald-600" title="Meaningful Interaction" data-id="element-204">
                      <CheckCircle2Icon className="w-5 h-5" data-id="element-205" />
                    </div>}
                </>}

              {/* Email specific badges */}
              {activity.type === 'email' && activity.emailStatus && <span className={`text-xs font-bold px-2.5 py-1 rounded-full border
                  ${activity.emailStatus === 'opened' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-gray-50 text-gray-600 border-gray-200'}`} data-id="element-206">
                  {activity.emailStatus === 'opened' ? 'Opened' : 'Sent'}
                </span>}

              {/* SMS specific badges */}
              {activity.type === 'sms' && activity.smsDirection && <span className={`inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full border
                  ${activity.smsDirection === 'inbound' ? 'bg-teal-50 text-teal-700 border-teal-200' : 'bg-gray-50 text-gray-600 border-gray-200'}`} data-id="element-207">
                  {activity.smsDirection === 'inbound' ? <ArrowDownLeftIcon className="w-3.5 h-3.5" data-id="element-208" /> : <ArrowUpRightIcon className="w-3.5 h-3.5" data-id="element-209" />}
                  {activity.smsDirection === 'inbound' ? 'Received' : 'Sent'}
                </span>}

              {/* Event specific badges */}
              {activity.type === 'event' && activity.eventType && <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200" data-id="element-210">
                  {activity.eventType}
                </span>}

              {/* Milestone specific badge */}
              {isMilestone && activity.milestoneType && <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-red-50 text-red-700 border border-red-200" data-id="element-211">
                  {activity.milestoneType}
                </span>}

              {/* Far-right affordance: a yellow star for milestones, expand carrot otherwise */}
              <div className="w-8 flex justify-center" data-id="element-212">
                {isMilestone ? <StarIcon className="w-5 h-5 text-red-500 fill-current" aria-label="File milestone" data-id="element-213" /> : <span className="text-gray-400" data-id="element-214">
                    {isExpanded ? <ChevronUpIcon className="w-5 h-5" data-id="element-215" /> : <ChevronDownIcon className="w-5 h-5" data-id="element-216" />}
                  </span>}
              </div>
            </div>
          </div>

          {/* Expanded Content with smooth height transition */}
          <div className="grid transition-all duration-300 ease-in-out" style={{
          gridTemplateRows: isExpanded ? '1fr' : '0fr'
        }} data-id="element-217">
            <div className="overflow-hidden" data-id="element-218">
              <div className="px-4 pb-4 pt-2 border-t border-gray-100 bg-gray-50/50" data-id="element-219">
                <div className="prose prose-sm max-w-none text-gray-600 mt-2" data-id="element-220">
                  <p className="whitespace-pre-wrap" data-id="element-221">
                    {activity.details ?? activity.summary}
                  </p>
                </div>

                {/* Action Links */}
                <div className="mt-4 flex gap-2" data-id="element-222">
                  <button className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-3 py-1.5 rounded-md flex items-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" onClick={e => e.stopPropagation()} data-id="element-223">
                    <ExternalLinkIcon className="w-4 h-4 mr-1.5" data-id="element-224" />
                    View Full Record
                  </button>
                  {activity.type === 'call' && activity.disposition === 'Connected' && <button className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-3 py-1.5 rounded-md flex items-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" onClick={e => e.stopPropagation()} data-id="element-225">
                        <PlayCircleIcon className="w-4 h-4 mr-1.5" data-id="element-226" />
                        Play Recording
                      </button>}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}